import { FunctionComponent, useEffect, useRef, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { ApolloError, useMutation, useQuery } from "@apollo/client";
import { TASK_BY_TASK_ID } from "../../api/query";
import { COMPLETE_POD_GOAL_TASK } from "../../api/mutation";
import BreadCumb from "../../components/common/Breadcrumb";
import Loader from "../../components/common/Loader";
import InputsNew from "../../components/common/InputsNew";
import DateSelector from "../../components/common/DateSelector";
import SearchableInputsDropdown from "../../components/common/SearchableDropdown";
import { showError, showSuccess } from "../../components/common/ToastNotification";
import backArrow from "../../assets/task/back_arrow.svg";
import InputsDropdownMultipleSearchSelect from "../../components/common/InputsDropdownMultipleSearchSelect";
import closeDowpDowniconimg from "../../assets/task/closedropdown-cicon.svg";

// TODO: task_by_task_id doesn't expose tag/doer like goal_details tasks did —
// using task_tags (tags_name) as the stand-in for flagging an agent slot.
const isAgentTask = (task: any) =>
  (task?.task_tags || []).some((t: any) =>
    (t?.tags_name || "").toLowerCase().includes("agent"),
  );

// TODO: task_by_task_id's checklist_items have `status`, not the `kind`/`ticked`
// pair goal_details returned. No signal here distinguishes agent-verified items
// from human ones, so everything is treated as human-checkable for now.
// Confirm the real field before relying on this.
const isChecklistItemTicked = (item: any) =>
  String(item?.status || "").toLowerCase() === "completed";

interface TaskAssigneeState {
  employee_id: string;
  employee_name: string;
  task_start_date: string | null;
  task_end_date: string | null;
  duration_hours: string;
  duration_minutes: string;
}

interface TaskChecklistState {
  [checklistIndex: number]: boolean;
}

const EditTaskTemplate: FunctionComponent = () => {
  const { userInfo } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();

  const state = location.state as {
    taskID?: any;
    projectID?: any;
    projectName?: string;
  };

  // task_id can arrive either via route param (/tasks/:taskId/edit) or via navigation state
  const taskID = params?.taskId || state?.taskID;

  // ── Read-only goal fields (populated from the GET call, never edited here) ──
  const [goalTitle, setGoalTitle] = useState("");
  const [goalStartDate, setGoalStartDate] = useState<string | null>(null);
  const [goalEndDate, setGoalEndDate] = useState<string | null>(null);
  const [goalDescription, setGoalDescription] = useState("");
  const [projectName, setProjectName] = useState(state?.projectName || "");
  // TODO: task_by_task_id has no pod_id/pod_name field — nothing to bind this to yet.
  const [podName, setPodName] = useState("");

  const [taskTitle, setTaskTitle] = useState("");
  const [taskData, setTaskData] = useState<any>(null);
  const [projectMembers, setProjectMembers] = useState

    <any>([]);
  const [goalSkills, setGoalSkills] = useState<
    { key: string; name: string }[]
  >([]);

  const [taskSkills, setTaskSkills] = useState<
    { key: string; name: string }[]
  >([]);
  const [taskNote, setTaskNote] = useState<string>("");

  // ── Editable: single task assignment ──
  const [assignee, setAssignee] = useState<TaskAssigneeState>({
    employee_id: "",
    employee_name: "",
    task_start_date: null,
    task_end_date: null,
    duration_hours: "",
    duration_minutes: "",
  });
  const [checklistState, setChecklistState] = useState<TaskChecklistState>({});

  const hydratedRef = useRef(false);

  // ── Fetch the task — task fields, checklist_items, and project members ──
  const {
    data: taskResponse,
    loading: taskLoading,
    error: taskError,
  } = useQuery(TASK_BY_TASK_ID, {
    variables: { task_id: Number(taskID) },
    fetchPolicy: "no-cache",
    skip: !taskID,
  });

  const [completePodGoalTask, { loading: updateLoading }] = useMutation(
    COMPLETE_POD_GOAL_TASK,
    {
      fetchPolicy: "no-cache",
    },
  );

  // ── Hydrate all state from the GET response, once ──
  useEffect(() => {
    if (hydratedRef.current) return;
    const task = taskResponse?.task_by_task_id;
    if (!task) return;
    hydratedRef.current = true;

    setTaskTitle(task.title || "");
    setTaskData(task);
    setTaskNote(task.task_note || "");
    setGoalTitle(task.goal_name || "");
    setPodName(task.pod_name || "");

    setGoalStartDate(task.goal_start_date ? task.goal_start_date.split("T")[0] : null);
    setGoalEndDate(task.goal_end_date ? task.goal_end_date.split("T")[0] : null);
    setGoalDescription(task.goal_description || "");
    setProjectName(task.project_name || projectName);

    const members = (task.project_members || []).map((m: any) => ({
      key: String(m.employee_id),
      name: m.member_name,
    }));
    setProjectMembers(members);

    const assignedMember = members.find(
      (m: any) => m.key === String(task.assigned_to_id),
    );

    setAssignee({
      employee_id: task.assigned_to_id ? String(task.assigned_to_id) : "",
      employee_name: assignedMember?.name || task.assigned_to_name || "",
      task_start_date: task.start_datetime ? task.start_datetime.split("T")[0] : null,
      task_end_date: task.end_datetime ? task.end_datetime.split("T")[0] : null,
      duration_hours: task.hours != null ? String(task.hours) : "",
      duration_minutes: task.mins != null ? String(task.mins) : "",
    });

    const goalSkillOptions = (task.goal_skills || []).map((skill: any) => ({
      key: String(skill.skill_id),
      name: skill.skill_name,
    }));

    setGoalSkills(goalSkillOptions);

    const existingTaskSkills = (task.skills || []).map((skill: any) => ({
      key: skill.skill_id,
      name: skill.skill_name,
    }));

    setTaskSkills(existingTaskSkills);


    const initialChecklist: TaskChecklistState = {};
    (task.checklist_items || []).forEach((item: any, cidx: number) => {
      initialChecklist[cidx] = isChecklistItemTicked(item);
    });
    setChecklistState(initialChecklist);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskResponse]);

  const handleAssigneeChange = (
    field: keyof TaskAssigneeState,
    value: string | null,
  ) => {
    setAssignee((prev) => ({ ...prev, [field]: value } as TaskAssigneeState));
  };

  const handleChecklistToggle = (checklistIdx: number) => {
    setChecklistState((prev) => ({
      ...prev,
      [checklistIdx]: !prev[checklistIdx],
    }));
  };

  // Only hours 0–24 and minutes 0–59, digits only
  const handleDurationChange = (
    field: "duration_hours" | "duration_minutes",
    raw: string,
  ) => {
    const digitsOnly = raw.replace(/[^0-9]/g, "");
    if (digitsOnly === "") {
      handleAssigneeChange(field, "");
      return;
    }
    const num = parseInt(digitsOnly, 10);
    if (field === "duration_minutes" && num > 59) return;
    if (field === "duration_hours" && num > 24) return;
    handleAssigneeChange(field, String(num));
  };

  const handleSubmit = async () => {
    if (!assignee.employee_id) {
      showError(`Please assign a member to "${taskTitle}"`);
      return;
    }
    if (!assignee.task_start_date) {
      showError(`Please select a start date for "${taskTitle}"`);
      return;
    }
    if (!assignee.task_end_date) {
      showError(`Please select an end date for "${taskTitle}"`);
      return;
    }

    const start = new Date(assignee.task_start_date);
    const end = new Date(assignee.task_end_date);

    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
      showError(`Please select valid dates for "${taskTitle}"`);
      return;
    }
    if (end < start) {
      showError(`End date cannot be before start date for "${taskTitle}"`);
      return;
    }
    if (goalStartDate && start < new Date(goalStartDate)) {
      showError(
        `Task start date for "${taskTitle}" cannot be before the goal start date`,
      );
      return;
    }

    const hours = Number(assignee.duration_hours || 0);
    const minutes = Number(assignee.duration_minutes || 0);

    if (!Number.isInteger(hours) || hours < 0 || hours > 24) {
      showError(`Please enter valid hours (0-24) for "${taskTitle}"`);
      return;
    }
    if (!Number.isInteger(minutes) || minutes < 0 || minutes > 59) {
      showError(`Please enter valid minutes (0-59) for "${taskTitle}"`);
      return;
    }
    if (hours === 0 && minutes === 0) {
      showError(`Please enter a duration for "${taskTitle}"`);
      return;
    }
    // if(taskNote.length == 0){
    //       showError(`Please enter a task note`);
    //     return;
    // }

    // Formats hours/minutes into "HH:MM:00" — defaults to "00:00:00" if left blank
    const formatTaskTime = (h: string, m: string) => {
      const hh = String(parseInt(h || "0", 10)).padStart(2, "0");
      const mm = String(parseInt(m || "0", 10)).padStart(2, "0");
      return `${hh}:${mm}:00`;
    };

    const taskTime = formatTaskTime(assignee.duration_hours, assignee.duration_minutes);

    const tickedChecklistIds = (taskData?.checklist_items || [])
      .map((item: any, cidx: number) =>
        checklistState[cidx] ? parseInt(item.id) : null,
      )
      .filter((id: number | null): id is number => id !== null);

    try {
      const res = await completePodGoalTask({
        variables: {
          task_id: parseInt(taskID),
          start_datetime: `${assignee.task_start_date}T00:00:00`,
          end_datetime: `${assignee.task_end_date}T${taskTime}`,
          ticked_checklist_ids: tickedChecklistIds,
          assigned_to: parseInt(assignee?.employee_id),
          skill_ids: taskSkills.map((skill) => parseInt(skill.key)),
          task_note: taskNote,
        },
      });

      const result = res.data?.complete_pod_goal_task;
      if (result?.status_code === 200) {
        showSuccess(result?.message || "Task updated successfully");
        navigate(-1);
      } else {
        showError(result?.message || "Failed to update task");
      }
    } catch (err) {
      if (err instanceof ApolloError) {
        showError(err.message);
      }
    }
  };

  if (taskError) {
    showError("Failed to load task details");
  }

  const handleSelectTaskSkill = (
    key: any,
    name: any,
    type?: any,
  ) => {
    if (key === "all") {
      if (name === true) {
        setTaskSkills([]);
      } else {
        setTaskSkills(goalSkills);
      }
      return;
    }

    setTaskSkills((prev) => {
      const exists = prev.some((item) => item.key === String(key));

      if (exists) {
        return prev.filter((item) => item.key !== String(key));
      }

      return [
        ...prev,
        {
          key: String(key),
          name,
        },
      ];
    });
  };

  const handleRemoveTaskSkill = (key: string) => {
    setTaskSkills((prev) =>
      prev.filter((item) => item.key !== key)
    );
  };

const handleTaskNoteChange = (value: string) => {
  setTaskNote(value);
};

  const isAgent = taskData ? isAgentTask(taskData) : false;

  return (
    <>
      <div
        id="cts-respon-p-dv"
        className="w-full relative min-h-screen overflow-hidden block flex-row items-start justify-center flex-wrap content-start py-40 px-20 mq991:p-8 mq575:p-4 box-border text-left text-base font-manrope"
      >
        <div className="flex-1 flex flex-col items-start justify-start gap-6">
          <BreadCumb clientName={projectName} />

          <div className="self-stretch flex flex-row items-center justify-start gap-[5px] text-2xl">
            <img
              className="w-6 relative h-6 overflow-hidden shrink-0 cursor-pointer dark-white-img"
              alt=""
              src={backArrow}
              onClick={() => navigate(-1)}
            />
            <h3 className="m-0 flex-1 relative text-inherit font-bold font-inherit mq450:text-base dark:text-white">
              Edit Task
            </h3>
          </div>

          <div className="self-stretch rounded-lg bg-surface-bg-primary dark:bg-slate-800 flex flex-col items-start justify-start gap-6 p-8 mq575:p-3">
            <div className="grid gap-4 assigntask-card-dv self-stretch">
              <div className="grid gap-4 inner-assigntask-card-dv self-stretch">
                {/* ── Goal Details — locked, populated from the GET call ── */}
                <h5 className="my-0 self-stretch text-lg dark:text-white font-bold flex items-center gap-2">
                  <span className="w-6 h-6 rounded-full bg-green-500 text-white text-xs flex items-center justify-center font-bold shrink-0">
                    ✓
                  </span>
                  Goal Details
                </h5>

                <div className="grid grid-cols-4 mq1275:grid-cols-3 mq950:grid-cols-2 mq575:grid-cols-1 gap-[8px] min-w-full">
                  <InputsNew
                    label="Project Name*"
                    type="text"
                    contentPlaceholder={projectName}
                    disabled={true}
                  />
                  <InputsNew
                    label="Pod*"
                    type="text"
                    contentPlaceholder={podName}
                    disabled={true}
                  />
                  <InputsNew
                    label="Goal Title*"
                    type="text"
                    contentPlaceholder={goalTitle}
                    value={goalTitle}
                    disabled={true}
                  />
                  <div className="flex flex-col items-start justify-start gap-[8px] startdate-inner-input">
                    <DateSelector
                      label="Start Date*"
                      selectedDate={goalStartDate}
                      onChange={() => { }}
                      disabled={true}
                      nothideParsedValue={true}
                    />
                  </div>
                  <div className="flex flex-col items-start justify-start gap-[8px] startdate-inner-input">
                    <DateSelector
                      label="End Date*"
                      selectedDate={goalEndDate}
                      onChange={() => { }}
                      disabled={true}
                      nothideParsedValue={true}
                    />
                  </div>
                </div>

                <div className="self-stretch">
                  <InputsNew
                    contentPlaceholder="Goal Description"
                    label="Description"
                    type="textarea"
                    value={goalDescription}
                    disabled={true}
                  />
                </div>
              {/* <div className="grid grid-cols-4 mq1275:grid-cols-3 mq950:grid-cols-2 mq575:grid-cols-1 gap-[8px] min-w-full">
                  <div className="status-filter pb-4  flex-wrap items-center grid  gap-[8px] multiple-dropdow-blg-dv">
                  <div className="self-stretch relative font-semibold dark:text-white">Goal Skills</div>
                  <div style={{display:"ruby"}} className="reportmulselect-blg  w-[303px] cts-flex1-remd contents flex-col  items-start justify-start text-left text-sm mq825:max-w-[100%] text-text-text-primary font-body-reg">
                    {goalSkills.map((ss: any) => (
                      <div
                        key={ss?.key}
                        className="reportmulselect-blg-dv rounded-radius-md1 mr-1 mb-1 overflow-hidden flex flex-row items-center justify-start gap-[8px] inner-item-select-dv border-[1px] border-solid border-surface-border dark:bg-slate-700"
                      >
                        <div className="flex flex-row items-center justify-start">
                          <div className="relative text-sm font-body-xs-semi-bold text-colors-text-black text-left inline-block dark:text-white">
                            {ss?.name}
                          </div>
                        </div>

                      </div>
                    ))}
                  </div>
                </div>
                </div> */}
              </div>
            </div>

            <div className="self-stretch border-t border-gray-200 dark:border-gray-700" />

            {/* ── Assign Task — the only editable section ── */}
            <div className="grid gap-4 assigntask-card-dv self-stretch">
              <div className="grid gap-4 inner-assigntask-card-dv self-stretch">
                <div className="grid gap-2">
                  <h5 className="my-0 self-stretch text-lg dark:text-white font-bold flex items-center gap-2">
                    <span className="w-6 h-6 rounded-full bg-surface-primary-surface text-white text-xs flex items-center justify-center font-bold shrink-0">
                      ✎
                    </span>
                    Assign Task
                  </h5>
                  <p className="my-0 text-sm text-gray-500 dark:text-gray-400">
                    Update the assignee, dates, duration, and checklist for this
                    task.
                  </p>
                </div>

                <div
                  className={`p-4 rounded-lg border ${isAgent
                      ? "border-purple-200 dark:border-purple-800 bg-purple-50 dark:bg-purple-900/10"
                      : "border-[1px] border-solid border-surface-border inner-assigntask-card-dv"
                    }`}
                >
                  <div className="flex items-center gap-3 mb-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-semibold dark:text-white">
                          {taskTitle}
                        </span>
                        {/* {isAgent && (
                          <span className="inline-flex items-center gap-1 text-xs bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300 px-2 py-0.5 rounded font-medium">
                            🤖 Agent Slot
                          </span>
                        )} */}
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-4 mq1400:grid-4 mq1275:grid-cols-3 mq950:grid-cols-2 mq575:grid-cols-1 mt-[8px] gap-[8px] min-w-full">
                    <SearchableInputsDropdown
                      label="Assign To"
                      initialValue={assignee.employee_name || ""}
                      placeHolder="Select member"
                      options={projectMembers.map((m: any) => ({
                        key: m.key,
                        name: m.name,
                      }))}
                      onSelect={(key: string, name: string) => {
                        handleAssigneeChange("employee_id", key);
                        handleAssigneeChange("employee_name", name);
                      }}
                    />
                    <DateSelector
                      label="Task Start Date"
                      selectedDate={assignee.task_start_date}
                      onChange={(date: string | null) =>
                        handleAssigneeChange("task_start_date", date)
                      }
                      minDate={goalStartDate ? new Date(goalStartDate) : undefined}
                      nothideParsedValue={true}
                    />
                    <DateSelector
                      label="Task End Date"
                      selectedDate={assignee.task_end_date}
                      onChange={(date: string | null) =>{
                      if (!date) {
                        handleAssigneeChange(
                          "task_end_date",
                          null
                        );
                        return;
                      }
                      const startDateValue = assignee?.task_start_date;
                          if (startDateValue && date !== startDateValue) {
                          showError(
                            `End Date must be the same as Start Date`
                          );
                          return;
                        }

                    handleAssigneeChange("task_end_date", date)
                      }           
                      }
                      minDate={
                        assignee.task_start_date
                          ? new Date(assignee.task_start_date)
                          : goalStartDate
                            ? new Date(goalStartDate)
                            : undefined
                      }
                      nothideParsedValue={true}
                    />

                    <div className="flex flex-col gap-[8px] duration-input-item-dv">
                      <label className="text-xs font-semibold dark:text-white">
                        Duration
                      </label>
                      <div className="flex items-center gap-2">
                        <div className="py-2.5 cursor-pointer w-12 self-stretch shadow-[0px_1px_2px_rgba(16,_24,_40,_0.05)] h-[21px] tabelin-input-dv rounded-radius-md bg-text-text-tertiary dark:text-white dark:bg-slate-700 overflow-hidden flex flex-row items-center justify-start px-[15px] border-[1px] border-solid border-surface-border">
                          <input
                            type="text"
                            inputMode="numeric"
                            maxLength={2}
                            placeholder="HH"
                            value={assignee.duration_hours ?? ""}
                            onChange={(e) =>
                              handleDurationChange("duration_hours", e.target.value)
                            }
                            className="w-full border-none outline-none focus-none text-center text-sm dark:text-white bg-dark-item"
                          />
                        </div>
                        {/* <span className="text-xs text-gray-500 dark:text-gray-400">
                          Hrs
                        </span> */}
                        <div className="py-2.5 cursor-pointer w-12 self-stretch shadow-[0px_1px_2px_rgba(16,_24,_40,_0.05)] h-[21px] tabelin-input-dv rounded-radius-md bg-text-text-tertiary dark:text-white dark:bg-slate-700 overflow-hidden flex flex-row items-center justify-start px-[15px] border-[1px] border-solid border-surface-border">
                          <input
                            type="text"
                            inputMode="numeric"
                            maxLength={2}
                            placeholder="MM"
                            value={assignee.duration_minutes ?? ""}
                            onChange={(e) =>
                              handleDurationChange("duration_minutes", e.target.value)
                            }
                            className="w-full border-none outline-none focus-none text-center text-sm dark:text-white bg-dark-item"
                          />
                        </div>
                        {/* <span className="text-xs text-gray-500 dark:text-gray-400">
                          Min
                        </span> */}
                      </div>
                    </div>
                  </div>

                  {/* <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-600">
                    <InputsDropdownMultipleSearchSelect
                      label="Skills"
                      placeHolder="Choose"
                      options={goalSkills}
                      onSelect={(key: any, name: any, type: any) =>
                        handleSelectTaskSkill(key, name, type)
                      }
                      inputType="checkbox"
                      checkedItems={taskSkills}
                    />
                  </div> */}
                  {/* {(taskSkills?.length ?? 0) > 0 && (
                    <div className="reportmulselect-blg flex-1 w-full cts-flex1-remd contents flex-col items-start justify-start text-left text-sm mq825:max-w-[100%] text-text-text-primary font-body-reg min-w-full">
                      <div className="inner-reportmulselect-blg inner-reportmulselect-item items-start justify-start text-left text-sm mq825:max-w-[100%] text-text-text-primary font-body-reg mt-2">
                        {taskSkills.map((ss: any) => (
                          <div
                            key={ss?.key}
                            className="reportmulselect-blg-dv rounded-radius-md1 overflow-hidden flex flex-row items-center justify-start gap-[8px] inner-item-select-dv border-[1px] border-solid border-surface-border dark:bg-slate-700"
                          >
                            <div className="flex flex-row items-center justify-start">
                              <div className="relative text-sm font-body-xs-semi-bold text-colors-text-black text-left inline-block dark:text-white">
                                {ss?.name}
                              </div>
                            </div>
                            <img
                              className="h-4 w-4 relative overflow-hidden shrink-0 cursor-pointer"
                              loading="lazy"
                              alt="Remove"
                              src={closeDowpDowniconimg}
                              onClick={() => handleRemoveTaskSkill(ss.key)}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  )} */}
                  <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-600">
                <InputsNew
                  label="Task Description*"
                  type="textarea"
                  contentPlaceholder="Add a description for this task..."
                  value={taskNote}
                  onChange={(e: any) => handleTaskNoteChange(e.target.value)}
                />
              </div>

                  {taskData?.checklist_items?.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-600">
                      <p className="my-0 text-xs font-semibold dark:text-white mb-2">
                        Checklist:
                      </p>
                      <div className="grid gap-1">
                        {taskData.checklist_items.map((item: any, cidx: number) => (
                          <label
                            key={item.id ?? cidx}
                            className="flex items-center gap-2 cursor-pointer py-[5px] px-[5px] border-[1px] border-solid border-surface-border"
                          >
                            {/* <input
                              type="checkbox"
                              checked={Boolean(checklistState[cidx])}
                              onChange={() => handleChecklistToggle(cidx)}
                            /> */}
                            <span className="text-xs text-gray-700 dark:text-gray-300">
                              {item.checklist_name}
                            </span>
                          </label>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <div className="self-stretch flex flex-row items-center justify-end gap-[8px]">
                  <button
                    type="button"
                    onClick={() => navigate(-1)}
                    className="cursor-pointer bg-white py-2.5 h-[44px] px-[42px] btn-text-purple rounded-radiuscomponent-button box-border flex flex-row items-center justify-center gap-[12px] border-[2px] border-solid border-surface-primary-surface hover:bg-gainsboro-100"
                  >
                    <span className="relative text-sm font-body-xs-semi-bold dark:text-white">
                      Cancel
                    </span>
                  </button>
                  <button
                    type="button"
                    onClick={handleSubmit}
                    disabled={updateLoading || taskLoading}
                    className="cursor-pointer [border:none] py-2.5 h-[44px] px-[42px] bg-surface-primary-surface rounded-radiuscomponent-button flex flex-row items-center justify-center gap-[12px] hover:bg-dodgerblue disabled:opacity-50"
                  >
                    <span className="relative text-sm font-body-xs-semi-bold text-surface-bg-primary">
                      Update Task
                    </span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {(taskLoading || updateLoading) && <Loader />}
    </>
  );
};

export default EditTaskTemplate;